import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useContext,
  useId,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { PART_FILE_UNITS, PartFileUnits } from '@frontend/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogFooter,
  Button,
  ButtonGroup,
  AlertDialogOverlay,
  Tooltip,
  Modal,
  Checkbox,
  FormControl,
  FormLabel,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalButtonGroup,
  ModalFooter,
  RadioGroup,
  Radio,
  useDisclosure,
  Text,
  Box,
  Flex,
  ModalOverline,
  OutlineCircleQuestionIcon,
  ModalCloseButton,
  HStack,
  Divider,
} from '@frontend/legacy-components';

import { MathInput, MathInputField } from 'components/MathInput';

import { DEFAULT_UNITS_BY_PREFERRED_SYSTEM } from 'constants/unitOfMeasure';

import { UnitSystemContext, UnitSystemProvider } from 'utils/measurement';

import { uploadDetailsModal } from './data/actions';
import {
  getCurrentUnits,
  getFilename,
  getIsModalOpen,
  getShouldEnterThickness,
  getShouldEnterUnits,
  getSimilarFiles,
  getThickness,
} from './data/reducer';

const MIN_THICKNESS = 0.0001;

const InnerUploadDetailsModal = () => {
  const dispatch = useDispatch();
  const filename = useSelector(getFilename);
  const isModalOpen: boolean = useSelector(getIsModalOpen);
  const shouldEnterThickness: boolean = useSelector(getShouldEnterThickness);
  const shouldEnterUnits: boolean = useSelector(getShouldEnterUnits);
  const similarFiles = useSelector(getSimilarFiles);
  const oldThickness: number | undefined = useSelector(getThickness);
  const currentUnits: string | undefined | null = useSelector(getCurrentUnits);

  const { togglePreferredUnitSystem, preferredUnitSystem } =
    useContext(UnitSystemContext)!;

  const isEditMode = oldThickness != null;

  const goBackButtonRef = useRef(null);
  const [applyToAll, setApplyToAll] = useState<boolean>(false);

  const defaultUnits =
    currentUnits ?? DEFAULT_UNITS_BY_PREFERRED_SYSTEM[preferredUnitSystem];
  const [units, setUnits] = useState(defaultUnits);

  const currentThickness = useMemo(() => {
    if (oldThickness == null) {
      return '';
    }

    return oldThickness.toString();
  }, [oldThickness]);

  const [thickness, setThickness] = useState(currentThickness);

  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();

  const closeUploadDetailsModal = useCallback(
    () => dispatch(uploadDetailsModal.close()),
    [dispatch],
  );

  const handleModalClose = isEditMode ? closeUploadDetailsModal : onAlertOpen;

  const handleApplyToAllChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setApplyToAll(e.target.checked);

  const thicknessAsNumber = parseFloat(thickness);
  const handleSubmit: React.FormEventHandler<HTMLElement> = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        uploadDetailsModal.confirm({
          applyToAll,
          units,
          thickness: thicknessAsNumber,
        }),
      );
    },
    [dispatch, applyToAll, units, thicknessAsNumber],
  );

  const isThicknessUnchanged = currentThickness === thickness;
  const isThicknessBelowMin = thicknessAsNumber < MIN_THICKNESS;
  const isSubmitButtonDisabled =
    shouldEnterThickness &&
    (isNaN(thicknessAsNumber) || isThicknessBelowMin || isThicknessUnchanged);

  const submitButtonTooltipLabel = isThicknessBelowMin
    ? `Material thickness must be greater than ${MIN_THICKNESS}`
    : thickness == null
    ? 'Material thickness is required'
    : undefined;

  const formId = useId();

  const initialFocusRef = useRef<HTMLElement>(null);

  return (
    <>
      <Modal
        size="sm"
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeOnOverlayClick={false}
        data-testid="part-details-modal"
        initialFocusRef={initialFocusRef}
      >
        <ModalContent>
          <ModalHeader>
            <ModalOverline>{filename}</ModalOverline>
            {`${isEditMode ? 'Edit' : 'Set'} File Details`}
            <HStack
              divider={<Divider orientation="vertical" height={3} />}
              position="absolute"
              right={2}
              top={1.5}
            >
              {shouldEnterThickness && (
                <Button variant="link" onClick={togglePreferredUnitSystem}>
                  Swap units
                </Button>
              )}
              <ModalCloseButton position="relative" top={0} right={0} />
            </HStack>
          </ModalHeader>
          <ModalBody
            id={formId}
            as="form"
            onSubmit={handleSubmit}
            alignItems="center"
            display="flex"
            flexDirection="column"
          >
            {shouldEnterUnits && (
              <Box flexBasis="50%" padding={1.25} color="feature.secondary">
                <Flex justify="center">
                  <FormControl>
                    <FormLabel>Units of measurement:</FormLabel>
                    <RadioGroup
                      value={units}
                      onChange={(val) => setUnits(val as PartFileUnits)}
                      ref={initialFocusRef as React.RefObject<HTMLDivElement>}
                    >
                      <Radio value={PART_FILE_UNITS.IN}>Inch</Radio>
                      <Radio value={PART_FILE_UNITS.MM}>Millimeter</Radio>
                    </RadioGroup>
                  </FormControl>
                </Flex>
              </Box>
            )}
            {shouldEnterThickness && (
              <FormControl maxWidth={28} isRequired>
                <FormLabel>{`Material thickness`}</FormLabel>
                <MathInput measure="length">
                  <MathInputField
                    ref={initialFocusRef as React.RefObject<HTMLInputElement>}
                    data-testid="upload-details-thickness"
                    defaultValue={thickness}
                    min={MIN_THICKNESS}
                    onChange={(e) => setThickness(e.target.value)}
                  />
                </MathInput>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter
            justifyContent={
              similarFiles && similarFiles.length > 1
                ? 'space-between'
                : undefined
            }
          >
            {similarFiles && similarFiles.length > 1 && (
              <Checkbox
                isChecked={applyToAll}
                onChange={handleApplyToAllChange}
                size="md"
              >
                {`Apply to ${similarFiles.length} similar files`}
                <Tooltip
                  label={
                    <>
                      If selected, these settings will apply to the following
                      files:
                      {similarFiles.map((file) => (
                        <span
                          key={`${file.file.name}.${file.file.lastModified}`}
                        >
                          <br />
                          {file.file.name}
                        </span>
                      ))}
                    </>
                  }
                  placement="right"
                >
                  <OutlineCircleQuestionIcon
                    ml={0.5}
                    boxSize={2}
                    color="feature.secondary"
                  />
                </Tooltip>
              </Checkbox>
            )}
            <ModalButtonGroup>
              <Button variant="secondary" onClick={handleModalClose}>
                Cancel
              </Button>
              <Tooltip
                isDisabled={!submitButtonTooltipLabel}
                label={submitButtonTooltipLabel}
              >
                <Button
                  data-testid="upload-details-submit"
                  type="submit"
                  form={formId}
                  isDisabled={isSubmitButtonDisabled}
                >
                  Save
                </Button>
              </Tooltip>
            </ModalButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AlertDialog
        leastDestructiveRef={goBackButtonRef}
        isOpen={isAlertOpen}
        onClose={onAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {`Cancel uploading ${filename}?`}
            </AlertDialogHeader>
            <AlertDialogBody gap={1}>
              <Text fontFamily="default">{`${filename} will not be uploaded.  If you are uploading additional files, you may be prompted to enter information about those.`}</Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={goBackButtonRef}
                  variant="secondary"
                  onClick={onAlertClose}
                >
                  Go Back
                </Button>
                <Button
                  colorScheme="destructive"
                  onClick={() => {
                    closeUploadDetailsModal();
                    onAlertClose();
                  }}
                >
                  Cancel Upload
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export const UploadDetailsModal = () => {
  const isModalOpen: boolean = useSelector(getIsModalOpen);

  if (!isModalOpen) {
    return null;
  }

  return (
    <UnitSystemProvider>
      <InnerUploadDetailsModal />
    </UnitSystemProvider>
  );
};
